/**
 * SalaryHistoryModal — view / edit / revert / delete salary-history entries
 * for a single employee (iter266).
 *
 * Rules enforced client-side (server is the source of truth):
 *  • Only MGT / SA see Edit + Delete + Revert actions.
 *  • Delete is offered only when `used_in_final_payroll === false`.
 *    Otherwise the row shows Revert (soft, audit-preserving).
 *  • Employee is identified by `employee_id` (immutable) — survives renames.
 */
import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { X, Save, RefreshCw, Trash2, Undo2, Loader2, AlertTriangle } from "lucide-react";
import { api } from "@/lib/api";

const _INR = (v) =>
  v == null || v === "" ? "-" :
  Number(v).toLocaleString("en-IN", { maximumFractionDigits: 2 });

const _STATUS_BADGE = {
  Active:     "bg-emerald-100 text-emerald-800 border-emerald-300",
  Historical: "bg-slate-100 text-slate-700 border-slate-300",
  Reverted:   "bg-amber-100 text-amber-800 border-amber-300",
};

export default function SalaryHistoryModal({
  open,
  onClose,
  session,
  employee,          // { employee_id, name, center, currentSalary }
  isAdmin,           // MGT/SA gate for edit/delete
  onChanged,         // optional callback so parent can refresh the roster
}) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [editRow, setEditRow] = useState(null); // { effective_from_month, new_salary, reason }

  const load = async () => {
    if (!open || !employee?.employee_id) return;
    setLoading(true);
    try {
      const res = await api.post("/salary-history/list-by-id", {
        token: session.token,
        employee_id: employee.employee_id,
      });
      setRows(res.data.history || []);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to load salary history");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); /* eslint-disable-line */ }, [open, employee?.employee_id]);

  const sortedRows = useMemo(
    () => [...rows].sort((a, b) => (a.effective_from_month || "").localeCompare(b.effective_from_month || "")),
    [rows]
  );

  const startEdit = (row) => setEditRow({
    effective_from_month: row.effective_from_month,
    new_salary: row.new_salary,
    reason: "",
    force: false,
  });

  const saveEdit = async () => {
    if (!editRow) return;
    if (!editRow.reason || editRow.reason.trim().length < 3) {
      toast.error("Correction reason (3+ chars) is required.");
      return;
    }
    try {
      await api.post("/salary-history/replace", {
        token: session.token,
        name: employee.name,
        employee_id: employee.employee_id || "",
        center: employee.center || "",
        effective_from_month: editRow.effective_from_month,
        new_salary: parseFloat(editRow.new_salary) || 0,
        reason: editRow.reason.trim(),
        force: !!editRow.force,
      });
      toast.success("Salary history updated");
      setEditRow(null);
      await load();
      onChanged?.();
    } catch (e) {
      const detail = e.response?.data?.detail;
      // FINAL-payroll block → offer force-replace confirmation.
      if (e.response?.status === 409 && detail?.code === "PAYROLL_FINALIZED") {
        const ok = window.confirm(
          `${detail.message}\n\nAffected month: ${detail.affected_month}\n\n` +
          `Click OK to force-replace anyway (only after reopening payroll).`
        );
        if (ok) {
          setEditRow({ ...editRow, force: true });
          setTimeout(saveEdit, 0);
        }
        return;
      }
      toast.error(typeof detail === "string" ? detail : detail?.message || "Update failed");
    }
  };

  const revert = async (row) => {
    const reason = window.prompt(
      `Revert salary rate effective ${row.effective_from_month} `
      + `(₹${_INR(row.new_salary)})?\n\nReason for revert (3+ chars):`,
      ""
    );
    if (!reason || reason.trim().length < 3) return;
    try {
      await api.post("/salary-history/revert", {
        token: session.token,
        name: employee.name,
        effective_from_month: row.effective_from_month,
        reason: reason.trim(),
      });
      toast.success("Salary entry reverted");
      await load();
      onChanged?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Revert failed");
    }
  };

  const del = async (row) => {
    const reason = window.prompt(
      `PERMANENTLY delete salary rate effective ${row.effective_from_month}?\n\n`
      + `This does NOT delete the employee, attendance, payroll or payslips.\n\n`
      + `Reason for delete (3+ chars):`,
      ""
    );
    if (!reason || reason.trim().length < 3) return;
    try {
      await api.post("/salary-history/delete", {
        token: session.token,
        name: employee.name,
        effective_from_month: row.effective_from_month,
        reason: reason.trim(),
      });
      toast.success("Salary entry deleted");
      await load();
      onChanged?.();
    } catch (e) {
      const detail = e.response?.data?.detail;
      if (e.response?.status === 409 && detail?.code === "PAYROLL_FINALIZED") {
        toast.error(
          `${detail.message}\n\nUse the Revert button instead.`,
          { duration: 6000 }
        );
        return;
      }
      toast.error(typeof detail === "string" ? detail : detail?.message || "Delete failed");
    }
  };

  if (!open) return null;

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4"
      data-testid="salary-history-modal"
      onClick={onClose}
    >
      <div
        className="bg-background w-full max-w-4xl max-h-[90vh] overflow-hidden rounded-lg border shadow-2xl flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between p-4 border-b">
          <div>
            <h2 className="text-lg font-semibold">
              Salary History — {employee?.name || "—"}
            </h2>
            <p className="text-xs text-muted-foreground">
              {employee?.center} · Employee ID:{" "}
              <span className="font-mono">{employee?.employee_id || "—"}</span>
            </p>
          </div>
          <Button
            variant="ghost"
            size="icon"
            onClick={onClose}
            data-testid="salary-history-close-btn"
          >
            <X className="w-4 h-4" />
          </Button>
        </div>

        <div className="p-4 flex-1 overflow-auto">
          {loading ? (
            <div className="flex items-center gap-2 text-muted-foreground">
              <Loader2 className="w-4 h-4 animate-spin" /> Loading…
            </div>
          ) : sortedRows.length === 0 ? (
            <div className="text-sm text-muted-foreground border rounded-md p-6 text-center">
              No salary history recorded yet.
              <br />
              Current salary in Employee Master:{" "}
              <strong>₹{_INR(employee?.currentSalary)}</strong>
            </div>
          ) : (
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b bg-muted/50">
                  <th className="text-left p-2">Effective From</th>
                  <th className="text-right p-2">Old → New</th>
                  <th className="text-left p-2">Status</th>
                  <th className="text-left p-2">Changed By</th>
                  <th className="text-left p-2">Changed At</th>
                  <th className="text-left p-2">Reason</th>
                  <th className="text-right p-2">Action</th>
                </tr>
              </thead>
              <tbody>
                {sortedRows.map((r) => {
                  const isEditing = editRow?.effective_from_month === r.effective_from_month;
                  return (
                    <tr
                      key={r.effective_from_month}
                      className="border-b hover:bg-muted/30"
                      data-testid={`salary-history-row-${r.effective_from_month}`}
                    >
                      <td className="p-2 font-mono">{r.effective_from_month}</td>
                      <td className="p-2 text-right">
                        <span className="text-muted-foreground">₹{_INR(r.old_salary)}</span>
                        <span className="mx-1">→</span>
                        {isEditing ? (
                          <Input
                            type="number"
                            value={editRow.new_salary}
                            onChange={(e) => setEditRow({ ...editRow, new_salary: e.target.value })}
                            className="w-28 h-8 inline-block"
                            data-testid={`salary-history-edit-amount-${r.effective_from_month}`}
                          />
                        ) : (
                          <strong>₹{_INR(r.new_salary)}</strong>
                        )}
                      </td>
                      <td className="p-2">
                        <Badge
                          variant="outline"
                          className={_STATUS_BADGE[r.status] || ""}
                          data-testid={`salary-history-status-${r.effective_from_month}`}
                        >
                          {r.status}
                        </Badge>
                        {r.used_in_final_payroll && (
                          <span
                            className="ml-1 inline-flex items-center gap-1 text-xs text-amber-700"
                            title="This rate has been used in a finalized payroll — destructive delete is blocked. Use Revert."
                          >
                            <AlertTriangle className="w-3 h-3" />
                            payroll-final
                          </span>
                        )}
                      </td>
                      <td className="p-2">{r.changed_by || "—"}</td>
                      <td className="p-2 text-xs">
                        {r.changed_at ? new Date(r.changed_at).toLocaleString() : "—"}
                      </td>
                      <td className="p-2 max-w-[200px] truncate" title={r.reason || ""}>
                        {r.reason || "—"}
                      </td>
                      <td className="p-2 text-right space-x-1 whitespace-nowrap">
                        {isEditing ? (
                          <>
                            <Input
                              placeholder="Reason (3+ chars)"
                              value={editRow.reason}
                              onChange={(e) => setEditRow({ ...editRow, reason: e.target.value })}
                              className="inline-block w-40 h-8 mr-1"
                              data-testid={`salary-history-edit-reason-${r.effective_from_month}`}
                            />
                            <Button
                              size="sm"
                              onClick={saveEdit}
                              data-testid={`salary-history-save-${r.effective_from_month}`}
                            >
                              <Save className="w-3 h-3 mr-1" />
                              Save
                            </Button>
                            <Button
                              size="sm"
                              variant="ghost"
                              onClick={() => setEditRow(null)}
                            >
                              Cancel
                            </Button>
                          </>
                        ) : (
                          isAdmin ? (
                            <>
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={() => startEdit(r)}
                                data-testid={`salary-history-edit-${r.effective_from_month}`}
                                disabled={r.status === "Reverted"}
                              >
                                Edit
                              </Button>
                              {r.used_in_final_payroll ? (
                                <Button
                                  size="sm"
                                  variant="outline"
                                  onClick={() => revert(r)}
                                  className="text-amber-700 border-amber-500"
                                  data-testid={`salary-history-revert-${r.effective_from_month}`}
                                  disabled={r.status === "Reverted"}
                                >
                                  <Undo2 className="w-3 h-3 mr-1" />
                                  Revert
                                </Button>
                              ) : (
                                <Button
                                  size="sm"
                                  variant="outline"
                                  onClick={() => del(r)}
                                  className="text-red-600 border-red-500"
                                  data-testid={`salary-history-delete-${r.effective_from_month}`}
                                >
                                  <Trash2 className="w-3 h-3 mr-1" />
                                  Delete
                                </Button>
                              )}
                            </>
                          ) : (
                            <span className="text-xs text-muted-foreground">View only</span>
                          )
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>

        <div className="border-t p-3 flex items-center justify-between bg-muted/30">
          <p className="text-xs text-muted-foreground max-w-[70%]">
            Salary-history edits never touch the employee, attendance, payroll,
            payslips, or transfer records. Rows already used by a FINAL payroll
            can only be reverted (audit-safe), never deleted.
          </p>
          <Button
            variant="outline"
            size="sm"
            onClick={load}
            data-testid="salary-history-refresh-btn"
          >
            <RefreshCw className="w-3 h-3 mr-1" />
            Refresh
          </Button>
        </div>
      </div>
    </div>
  );
}
